/**
 * Vehicles feature (home, results, vehicle page, reserve sheets, search sheet). French copied verbatim from the
 * components it replaces (CONVENTIONS §12): straight and curly apostrophes as they were.
 */
export const fr = {
  /* Shared within the feature */
  checkConnection: 'Vérifiez votre connexion, puis réessayez.',
  'kind.label': 'Type de véhicule',
  categories: 'Catégories',
  perDay: ' / jour',
  models: { one: '{count} modèle', other: '{count} modèles' },
  modelsMore: { one: 'Plus de {count} modèle', other: 'Plus de {count} modèles' },
  seats: { one: '{count} place', other: '{count} places' },
  specs: { one: '{transmission} · {fuel} · {count} place', other: '{transmission} · {fuel} · {count} places' },
  'error.vehicles': 'Impossible de charger les véhicules',
  'reserve.cta': 'Réserver',
  'reserve.chooseReturn': 'Choisissez une date de retour.',
  'similar.error': 'Suggestions indisponibles',
  deliveryHint: "L'agence vous apporte le véhicule",

  /* Search fields (hero bar, booking card, search sheet) */
  'search.place': 'Lieu de prise en charge',
  'search.placeAria': 'Lieu de prise en charge : {city}. Modifier la recherche',
  'search.pickup': 'Départ',
  'search.pickupAria': 'Départ : {when}. Modifier la recherche',
  'search.return': 'Retour',
  'search.returnAria': 'Retour : {when}. Modifier la recherche',
  'search.dates': 'Dates',
  'search.datesAria': 'Dates : {dates}. Modifier la recherche',
  'search.submit': 'Rechercher',
  'time.earlier': '{label} plus tôt',
  'time.later': '{label} plus tard',
  'grace.late': 'Retour à partir de {time} : une journée supplémentaire est comptée.',
  'grace.ok': 'Retour avant {time} : aucune journée supplémentaire.',
  'calendar.chooseReturn': 'Choisissez le retour',
  'calendar.restart': 'Touchez une date pour recommencer',

  /* Home: hero */
  'hero.imageAlt': "Piste de la vallée de l'Ourika face aux sommets enneigés du Haut Atlas",
  'hero.tag': 'Agences vérifiées dans 20 villes du Maroc',
  'hero.titleLead': 'Votre Maroc,',
  'hero.titleAccent': 'clés en main.',
  'hero.lede':
    "Voitures et motos auprès d'agences vérifiées, de l'aéroport à la médina. Le prix final s'affiche avant de réserver, et votre carte n'est débitée qu'une fois l'agence confirmée.",
  'hero.tick.delivery': 'Livraison à votre adresse',
  'hero.tick.refund': "Remboursement intégral jusqu'à 48 h avant",
  'hero.tick.support': 'Assistance 24/7',
  'trust.agencies': 'Agences contrôlées par Cardnd',
  'trust.charge': 'Débit seulement à la confirmation',
  'trust.price': 'Prix total affiché, frais compris',
  'showcase.open': 'Voir les véhicules disponibles à {city}',
  'showcase.live': 'Disponibles maintenant · {city}',
  'showcase.note': "<b>Carte pré-autorisée</b>, débitée seulement quand l'agence confirme",

  /* Home: sections */
  'categories.title': 'Trouvez le bon <serif>véhicule.</serif>',
  'categories.error': 'Impossible de charger les catégories',
  'categories.allMotos': 'Toutes les motos',
  'categories.from': 'dès {price} / jour',
  'rail.live': 'En direct',
  'rail.title': 'Disponibles à <serif>{city}.</serif>',
  'rail.lead': 'Prix total pour {days} ({dates}), affiché avant de réserver.',
  'rail.all': 'Tout voir',
  'how.eyebrow': 'Comment ça marche',
  'how.title': 'Réserver sur Cardnd, <serif>sans mauvaise surprise.</serif>',
  'how.lead':
    'Vous réservez un modèle. Cardnd transmet votre demande aux agences vérifiées ; la première qui confirme vous remet le véhicule.',
  'how.1.title': 'Choisissez votre modèle',
  'how.1.text': "Le prix total s'affiche avant de réserver, frais de service compris. Pas de surprise au comptoir.",
  'how.2.title': 'Une agence vérifiée confirme',
  'how.2.text': "Votre carte est pré-autorisée, pas débitée. Elle n'est débitée qu'au moment où l'agence accepte.",
  'how.3.title': 'Présentez votre bon',
  'how.3.text': "QR code et code PIN à l'agence, ou livraison à votre adresse. La caution est gérée par l'agence.",
  'teaser.pill': 'Nouveau · Expériences',
  'teaser.title': 'Au-delà de la route, <serif>le Maroc à vivre.</serif>',
  'teaser.text':
    "Désert d'Agafay, vallée de l'Ourika, Toubkal… Des expériences encadrées par des guides locaux, à réserver en quelques minutes.",
  'teaser.cta': 'Découvrir les expériences',
  'teaser.from': 'dès {price} {unit}',
  'destinations.eyebrow': 'Destinations',
  'destinations.title': 'Partout où vous <serif>atterrissez.</serif>',
  'destinations.hint': 'Nombre de véhicules disponibles par ville',
  'destinations.error': 'Impossible de charger les destinations',
  'partner.eyebrow': 'Agences de location',
  'partner.title': 'Louez votre flotte <serif>sans courir après les clients.</serif>',
  'partner.text':
    'Recevez des demandes déjà pré-autorisées, acceptez celles qui vous conviennent et remettez le véhicule sur présentation du bon.',
  'partner.cities': 'villes couvertes au Maroc',
  'partner.first': '1<sup>re</sup>',
  'partner.firstText': 'agence qui accepte remporte la réservation',
  'partner.all': '100 %',
  'partner.allText': 'des demandes pré-autorisées',

  /* Results */
  'results.filters': 'Filtres',
  'results.datesUpdated': 'Dates mises à jour : {dates}',
  'results.title': '{models} <serif>à {city}</serif>',
  'results.lead': 'Prix total pour {days}, affiché avant de réserver · agence vérifiée attribuée à la confirmation',
  'results.empty.title': 'Aucun modèle ne correspond',
  'results.empty.reset': 'Réinitialiser les filtres',
  'results.empty.change': 'Changer de dates ou de ville',
  'results.empty.filtered': 'Élargissez vos filtres, ou changez de dates ou de ville.',
  'results.empty.text':
    "Aucun véhicule n'est disponible à {city} sur ces dates. Essayez d'autres dates ou une autre ville.",
  'results.more': 'Voir plus de modèles',
  'results.moreError': 'Les modèles suivants n’ont pas pu être chargés : réessayez.',
  'results.note.title': 'Vous réservez un modèle, pas une agence au hasard.',
  'results.note.text':
    "Cardnd transmet votre demande aux agences vérifiées de {city}. La première qui confirme vous remet le véhicule, et votre carte n'est débitée qu'à ce moment-là.",
  'filters.both': 'Les deux',
  'filters.close': 'Fermer les filtres',
  'filters.gearbox': 'Boîte de vitesses',
  'filters.sort': 'Trier par',
  'filters.clear': 'Effacer tout',
  'filters.show': 'Afficher {models}',
  'filters.showResults': 'Afficher les résultats',
  'sort.priceAsc': 'Prix croissant',
  'sort.priceDesc': 'Prix décroissant',
  'sort.depositAsc': 'Caution la plus basse',
  'longStay.title': 'Restez 7 jours, payez moins par jour',
  'longStay.text': "La remise longue durée s'applique automatiquement dès 7 jours, puis dès 28 jours.",
  'longStay.cta': 'Passer à 7 jours',
  'seo.results.car': 'Location de voitures à {city}',
  'seo.results.moto': 'Location de motos à {city}',
  'seo.results.carCategory': 'Location de voitures à {city} · {category}',
  'seo.results.motoCategory': 'Location de motos à {city} · {category}',
  'seo.results.description':
    'Véhicules disponibles à {city} : prix total affiché avant de réserver, agences de location vérifiées, paiement sécurisé.',
  'seo.results.descriptionCount':
    '{models} disponibles à {city} : prix total affiché avant de réserver, agences de location vérifiées, paiement sécurisé.',

  /* Vehicle page */
  'vehicle.gone.title': 'Ce véhicule n’est plus disponible',
  'vehicle.gone.cta': 'Voir les véhicules disponibles',
  'vehicle.gone.text': 'Il a peut-être été retiré par l’agence. D’autres modèles vous attendent à {city}.',
  'vehicle.error.title': 'Impossible d’afficher ce véhicule',
  'vehicle.loading': 'Chargement du véhicule',
  'vehicle.crumb': "Fil d'Ariane",
  'vehicle.crumbResults': 'Résultats à {city}',
  'vehicle.where': "à {city} · ou similaire selon l'agence qui confirme",
  'vehicle.booking': 'Réservation',
  'seo.vehicle.title': 'Location {name} à {city}',
  'seo.vehicle.description': {
    one: 'Louez {vehicle} à {city} auprès d’une agence vérifiée : {count} place, {transmission}, dès {price} MAD par jour. Votre carte n’est débitée qu’à la confirmation.',
    other:
      'Louez {vehicle} à {city} auprès d’une agence vérifiée : {count} places, {transmission}, dès {price} MAD par jour. Votre carte n’est débitée qu’à la confirmation.',
  },
  'jsonLd.description': {
    one: '{name} à louer à {city} : {count} place, {transmission}, {fuel}. Agence vérifiée attribuée à la confirmation.',
    other:
      '{name} à louer à {city} : {count} places, {transmission}, {fuel}. Agence vérifiée attribuée à la confirmation.',
  },
  'jsonLd.home': 'Accueil',
  'jsonLd.results': 'Location à {city}',
  'reserve.checkFailed': 'La disponibilité n’a pas pu être vérifiée. Vérifiez votre connexion, puis réessayez.',
  'reserve.signedIn': 'Connexion réussie',
  'reserve.minStay': {
    one: 'Minimum {count} jour pour ce véhicule : allongez la location.',
    other: 'Minimum {count} jours pour ce véhicule : allongez la location.',
  },
  'gallery.similar': 'ou modèle similaire',
  'gallery.share': 'Partager',
  'gallery.copied': 'Lien de la fiche copié.',
  'gallery.alt1': 'Route du Haut Atlas',
  'gallery.alt2': 'Ruelle de médina',

  /* Booking card */
  'card.tier': { one: 'Dès {count} jour : {price} / jour', other: 'Dès {count} jours : {price} / jour' },
  'card.deposit': 'Caution',
  'card.toChoose': 'À choisir',
  'card.yourDates': 'Vos dates',
  'card.period': 'Période',
  'card.booked': 'Déjà réservé',
  'card.conflictDay': 'Ce modèle est déjà réservé le {day} : choisissez d’autres dates.',
  'card.conflict': 'Ce modèle n’est pas disponible sur ces dates : choisissez d’autres dates.',
  'card.longStay': 'Remise longue durée',
  'card.notCharged': 'Vous ne serez pas encore débité',
  'meter.max': '{days} jours et plus',
  'meter.progress': '{days} / {target} jours',
  'meter.add': 'Ajoutez {days} : la remise s’applique automatiquement.',
  'meter.applied': 'Remise appliquée. Dès {days} jours, la remise augmente encore.',
  'meter.maxApplied': 'Remise maximale appliquée.',

  /* Vehicle sections */
  'included.title': 'Ce que comprend cette location',
  'agency.title': 'Votre agence',
  'agency.assigned': 'Attribuée à la confirmation',
  'agency.oneOf': 'Une agence vérifiée de {city}',
  'agency.text':
    'Vous réservez le modèle. Cardnd transmet votre demande aux agences partenaires disponibles ; la première qui confirme vous envoie son bon de réservation. Si aucune ne confirme, la pré-autorisation est libérée.',
  'agency.checked': 'Agences contrôlées',
  'agency.voucher': 'Bon QR + code PIN',
  'agency.chat': 'Échanges via Cardnd',
  'agency.zone': '{city} · zone indicative',
  'agency.zoneText': 'Adresse exacte communiquée après la réservation. Livraison possible à votre adresse.',
  'policies.title': 'Politiques',
  'policies.cancel': 'Annulation',
  'policies.for': 'pour un départ le {day} à {time}',
  'policies.error': 'Conditions indisponibles',
  'policies.rules': "Règles de l'agence",
  'reviews.title': 'Avis',
  'reviews.hint': 'Notés après chaque location terminée',
  'reviews.average': 'Note moyenne',
  'reviews.count': { one: '{count} avis', other: '{count} avis' },
  'reviews.empty.title': "Pas encore d'avis",
  'reviews.empty.text':
    'Aucun avis pour ce modèle à {city} pour l’instant. Après votre location, vous noterez cinq critères.',
  'reviews.notRated': 'pas encore noté',
  'criteria.service': 'Service',
  'criteria.condition': 'État du véhicule',
  'criteria.value': 'Rapport qualité-prix',
  'criteria.performance': 'Performance',
  'criteria.cleanliness': 'Propreté',
  'similar.title': 'Aussi disponibles à {city}',

  /* Time confirm sheet */
  'times.title': 'Confirmez vos horaires',
  'times.daysEstimate': '{days} · estimation',
  'times.pickupTime': 'Heure de départ',
  'times.returnTime': 'Heure de retour',
  'times.billed': 'Durée facturée',
  'times.extraDay': '+1 jour',
  'times.graceLate':
    'Retour à partir de {time} : une journée supplémentaire est comptée (2 h de tolérance après l’heure de départ).',
  'times.graceOk': 'Retour avant {time} : aucune journée supplémentaire (2 h de tolérance après l’heure de départ).',
  'times.noSlot': 'Plus aucun départ possible aujourd’hui.',
  'times.tomorrow': 'Partir demain',
  'times.past': 'Cet horaire est passé : choisissez un départ à partir de {time}.',

  /* Alternatives sheet */
  'alt.blackoutTitle': 'Pas de réservation sur ces dates',
  'alt.title': 'Ce modèle est déjà réservé',
  'alt.changeDates': 'Changer de dates',
  'alt.all': 'Tous les véhicules à {city}',
  'alt.blackout': 'Cardnd ne prend pas de réservation sur cette période à {city}. Choisissez d’autres dates.',
  'alt.text':
    '{name} n’est plus disponible sur vos dates. Ces modèles sont libres à {city}, aux mêmes dates et horaires.',
  'alt.loading': 'Recherche des modèles disponibles',
  'alt.none': 'Aucun autre modèle libre à {city}',
  'alt.noneText': 'Essayez d’autres dates ou d’autres horaires.',

  /* Sign-in sheet */
  'signIn.eyebrow': 'Dernière étape avant le paiement',
  'signIn.title': 'Connectez-vous pour réserver',
  'signIn.text':
    'Vos réservations, vos bons et vos paiements sont rattachés à votre compte. Vos dates et horaires sont conservés.',
  'signIn.email': 'E-mail',
  'signIn.emailPlaceholder': 'vous@exemple.com',
  'signIn.password': 'Mot de passe',
  'signIn.passwordPlaceholder': 'Votre mot de passe',
  'signIn.forgot': 'Mot de passe oublié ?',
  'signIn.submit': 'Se connecter et continuer',
  'signIn.noAccount': 'Pas encore de compte ?',
  'signIn.error.emailRequired': 'Saisissez votre e-mail.',
  'signIn.error.emailInvalid': 'Format d’e-mail invalide.',
  'signIn.error.passwordRequired': 'Saisissez votre mot de passe.',

  /* Search sheet */
  'sheet.eyebrow': 'Recherche',
  'sheet.title': 'Où et quand partez-vous ?',
  'sheet.show': { one: 'Afficher {count} modèle', other: 'Afficher {count} modèles' },
  'sheet.hours': 'Heures',
  'sheet.delivery': 'Livraison à mon adresse',
} as const;
